import React from 'react'
// libs
import { formValueSelector, reduxForm } from 'redux-form'
import { useSelector } from 'react-redux'
import { useMutation } from '@apollo/client/react'
// components
import { ReduxSwitch } from 'components/FormElements/ReduxForm'
import { Header } from 'components/Modals/ConfirmDeleteModal'
// view components
import { Button, BUTTON_LAYOUTS, Modal } from 'components/UI'
// hooks
import { useModalData } from 'hooks/index'
// types
import { InjectedFormProps } from 'redux-form/lib/reduxForm'
// constants
import { Forms, Modals } from 'constants/index'
// graphql
import { INJURY_MASTER_DISCONNECT_MUTATION } from 'graphql/injuryMaster/mutations'
import { INJURY_MASTER_IS_CONFIGURED_QUERY, INJURY_MASTER_SETTINGS_QUERY } from 'graphql/injuryMaster/queries'
// views
import { ActionsWrapper, Content, ContentText } from 'themes/views/modals'
// themes
import { colors } from 'themes'
// utils
import { handleError } from 'utils/errors/handlers'

type Fields = {
  clearLinks: boolean
}

const textStyle = { fontWeight: 500 }
const warningStyle = { color: colors.red300, fontSize: '14px' }

const selector = formValueSelector(Forms.INJURY_MASTER_REVOKE_FORM)

const InjuryMasterRevokeModal: React.FC<InjectedFormProps<Fields>> = ({ handleSubmit, reset }) => {
  const [disconnect] = useMutation(INJURY_MASTER_DISCONNECT_MUTATION)

  const [isLoading, setLoading] = React.useState(false)

  const clearLinks: boolean = useSelector((state) => selector(state, 'clearLinks'))

  const {
    hideModal,
    modalData: { isOpen }
  } = useModalData({ modalType: Modals.INJURY_MASTER_REVOKE_MODAL })

  // The modal stays mounted between opens, so each opening starts with the switch off.
  React.useEffect(() => {
    if (isOpen) {
      reset()
    }
  }, [isOpen])

  const handleFormSubmit = async ({ clearLinks }: Fields) => {
    try {
      setLoading(true)

      await disconnect({
        variables: { input: { clearLinks: !!clearLinks } },
        refetchQueries: [INJURY_MASTER_SETTINGS_QUERY, INJURY_MASTER_IS_CONFIGURED_QUERY]
      })

      hideModal()
    } catch (error) {
      handleError(error, 'InjuryMasterRevokeModal.handleFormSubmit')
    } finally {
      setLoading(false)
    }
  }

  return (
    // The dialog's paper is the form: a wrapping <form> would break the dialog's flex layout.
    <Modal
      hideModal={hideModal}
      isOpen={isOpen}
      PaperProps={{ component: 'form', onSubmit: handleSubmit(handleFormSubmit) }}>
      <Header>Revoke Injury Master</Header>
      <Content>
        <ContentText color={'inherit'} style={textStyle}>
          Are you sure you want to revoke the Injury Master connection?
        </ContentText>
        {/* Off keeps the records' Injury Master ids, so connecting again resumes without duplicates. */}
        <ReduxSwitch name='clearLinks' label='Clear synced records' data-testid='injury-master-revoke-clear-links' />
        {clearLinks && (
          <ContentText style={warningStyle}>
            The next sync will create every record again. Remove them in Injury Master first to avoid duplicates.
          </ContentText>
        )}
      </Content>
      <ActionsWrapper>
        <Button shadowLess isLoading={isLoading} layout={BUTTON_LAYOUTS.RED} type={'submit'}>
          Confirm
        </Button>
        <Button shadowLess layout={BUTTON_LAYOUTS.RED_INVERTED} onClick={hideModal}>
          Cancel
        </Button>
      </ActionsWrapper>
    </Modal>
  )
}

export default reduxForm<Fields>({
  form: Forms.INJURY_MASTER_REVOKE_FORM,
  initialValues: { clearLinks: false }
})(InjuryMasterRevokeModal)
